import * as React from "react"
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
    onReset?: () => void
}

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    onReset,
    ...props
}: CalendarProps) {
    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn("p-3", className)}
            classNames={{
                months: "flex flex-col gap-4",
                month: "flex flex-col gap-4",
                month_caption: "flex items-center justify-between px-1 h-8",
                caption_label: "text-sm font-medium text-foreground",
                nav: "flex items-center gap-1",
                button_previous: cn(
                    "h-7 w-7 inline-flex items-center justify-center rounded-md border border-input bg-background p-0 hover:bg-accent text-foreground"
                ),
                button_next: cn(
                    "h-7 w-7 inline-flex items-center justify-center rounded-md border border-input bg-background p-0 hover:bg-accent text-foreground"
                ),
                month_grid: "w-full border-collapse",
                weekdays: "flex",
                weekday: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] text-center",
                week: "flex w-full mt-2",
                day: cn(
                    "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-primary/10 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-primary/5",
                    props.mode === "range"
                        ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
                        : "[&:has([aria-selected])]:rounded-md"
                ),
                day_button: cn(
                    "h-9 w-9 p-0 font-normal rounded-md inline-flex items-center justify-center",
                    "text-foreground hover:bg-accent hover:text-foreground",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    "aria-selected:opacity-100"
                ),
                range_start: "day-range-start rounded-l-md bg-primary text-primary-foreground",
                range_end: "day-range-end rounded-r-md bg-primary text-primary-foreground",
                selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                today: "bg-accent text-foreground font-semibold",
                outside: "day-outside text-muted-foreground opacity-50 aria-selected:bg-primary/10 aria-selected:text-muted-foreground",
                disabled: "text-muted-foreground opacity-50 cursor-not-allowed",
                range_middle: "aria-selected:bg-primary/10 aria-selected:text-foreground",
                hidden: "invisible",
                ...classNames,
            }}
            components={{
                Chevron: ({ orientation }) => {
                    const Icon = orientation === "left" ? ChevronLeft : ChevronRight
                    return <Icon className="h-4 w-4" />
                },
                MonthCaption: ({ calendarMonth }) => (
                    <div className="flex items-center justify-between w-full">
                        <span className="text-sm font-medium text-foreground">
                            {calendarMonth.date.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                        </span>
                        {onReset && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    onReset()
                                }}
                                className="h-6 px-2 inline-flex items-center justify-center gap-1 rounded-md border border-input bg-background text-xs hover:bg-accent text-muted-foreground hover:text-foreground"
                                title="Reset to last 7 days"
                            >
                                <RotateCcw className="h-3 w-3" />
                                <span>Reset</span>
                            </button>
                        )}
                    </div>
                ),
            }}
            {...props}
        />
    )
}
Calendar.displayName = "Calendar"

export { Calendar }
